import * as React from "react";
import { Button } from "@/components/_components/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/_components/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/_components/command";
import { Command as CommandPrimitive } from "cmdk";
import { Check, ChevronsUpDown, Search } from "lucide-react";
import { cn } from "@/lib/utils";

export type Option = {
  label?: string;
  value?: string;
};

interface DropdownSearchProps {
  id?: string;
  options: Option[];
  value?: string;
  placeholder?: string;
  disabled?: boolean;
  onChange: (value: string) => void;
  onSearchChange?: (searchTerm: string) => void;
  isSearchable?: boolean;
  isLoading?: boolean;
}

export const DropdownSearchCombobox = React.forwardRef<
  HTMLButtonElement,
  DropdownSearchProps
>(
  (
    { id, options = [], value, placeholder = "Select an option", disabled = false, onChange, onSearchChange, isSearchable = false, isLoading = false },
    ref
  ) => {
    const [open, setOpen] = React.useState(false);
    const [searchTerm, setSearchTerm] = React.useState("");

    const selectedLabel = options.find((opt) => opt.value === value)?.label;

    const handleSearchChange = (newSearchTerm: string) => {
      setSearchTerm(newSearchTerm);
      if (isSearchable && onSearchChange) {
        onSearchChange(newSearchTerm);
      }
    };

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            ref={ref}
            id={id}
            variant="outline"
            role="combobox"
            disabled={disabled}
            className={cn(
              "justify-between w-full h-11 rounded-lg border border-input bg-background px-4 py-2.5 text-sm text-foreground ring-offset-background hover:border-input/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2 focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200",
              !value && "text-muted-foreground/70"
            )}
          >
            <span className="truncate text-left flex-1">
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Loading...
                </span>
              ) : (
                selectedLabel || placeholder
              )}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent 
          className="w-[var(--radix-popover-trigger-width)] p-0 rounded-lg" 
          align="start"
          sideOffset={4}
        >
          <Command className="rounded-lg">
            <div className="flex items-center border-b border-border px-3">
              {isLoading ? (
                <svg className="animate-spin mr-2 h-4 w-4 shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : (
                <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
              )}
              <CommandPrimitive.Input
                placeholder="Search..."
                value={searchTerm}
                onValueChange={handleSearchChange}
                className="flex h-11 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50 m-[5px]"
              />
            </div>
            <CommandEmpty className="py-6 text-center text-sm">
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Searching...
                </span>
              ) : isSearchable && searchTerm.length < 3 ? (
                "Type at least 3 characters to search"
              ) : (
                "No results found."
              )}
            </CommandEmpty>
            <CommandGroup className="max-h-[300px] overflow-y-auto p-1">
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.label}
                  onSelect={() => {
                    onChange(option?.value ?? "");
                    setOpen(false);
                  }}
                  className="cursor-pointer"
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      option.value === value ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <span className="flex-1">{option.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </Command>
        </PopoverContent>
      </Popover>
    );
  }
);

DropdownSearchCombobox.displayName = "DropdownSearchCombobox";